import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { testimonials } from '../../data/testimonialsData';
import './TestimonialsSpotlight.css';

// Matches the HomeV2 panel's own slide-in transition duration — the
// spotlight stays hidden while the panel is still sliding, same reasoning
// as the work/publications panels.
const PANEL_SETTLE_DELAY = 650;
const AUTO_ADVANCE_MS = 7000;

const TestimonialsSpotlight: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  // The quote only renders (and fades in) once the container's own
  // entrance fade has actually begun, so the two don't visually collide.
  const [revealReady, setRevealReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const current = testimonials[activeIndex];

  // Entrance: the whole spotlight slides/fades in once the panel itself
  // has settled, then the first quote decrypts into view.
  useEffect(() => {
    const el = containerRef.current;
    if (el) gsap.set(el, { opacity: 0, x: 60 });

    const timer = setTimeout(() => {
      if (el) gsap.to(el, { opacity: 1, x: 0, duration: 0.55, ease: 'power3.out' });
      setRevealReady(true);
    }, PANEL_SETTLE_DELAY);

    return () => clearTimeout(timer);
  }, []);

  // Auto-advance through testimonials; paused on hover, and the timer
  // resets whenever a thumbnail is clicked (activeIndex changes).
  useEffect(() => {
    if (isHovered) return;
    const timer = setTimeout(() => {
      setActiveIndex((i) => (i + 1) % testimonials.length);
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
  }, [activeIndex, isHovered]);

  return (
    <div
      className="testi-spotlight"
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="testi-quote-area"
        style={{ '--testi-accent': current.color || 'var(--text-color)' } as React.CSSProperties}
      >
        <span className="testi-quote-mark" aria-hidden="true">&ldquo;</span>
        {revealReady && (
          <p className="testi-quote testi-quote-fade" key={activeIndex}>
            {current.quote}
          </p>
        )}

        <div className="testi-person">
          <span className="testi-name">{current.name}</span>
          <span className="testi-role">{current.designation}</span>
          {current.linkedin && (
            <a href={current.linkedin} target="_blank" rel="noopener noreferrer" className="testi-link">
              view linkedin
              <span className="testi-arrow">→</span>
            </a>
          )}
        </div>
      </div>

      <div className="testi-thumbs">
        {testimonials.map((t, i) => (
          <button
            key={i}
            className={`testi-thumb ${i === activeIndex ? 'is-active' : ''}`}
            style={{ '--testi-accent': t.color || 'var(--text-color)' } as React.CSSProperties}
            onClick={() => setActiveIndex(i)}
            aria-label={`Show testimonial from ${t.name}`}
          >
            <img src={t.src} alt={t.name} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default TestimonialsSpotlight;
